import React, { useState } from 'react';
import './GroupTable.css';

const GroupTable = ({ groups, onAddSubgroup, onRemoveUser, onRefresh }) => {
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const toggleGroupExpansion = (groupName) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName);
    } else {
      newExpanded.add(groupName);
    }
    setExpandedGroups(newExpanded);
  };

  const formatMembersList = (members) => {
    if (!members || members.length === 0) {
      return 'No members';
    }
    
    // Extract names from DNs before joining
    const memberNames = members.map(member => {
      return member.includes('cn=users') ? member.split(',')[0].replace('uid=', '') :
             member; // Should only be users here now
    });

    if (memberNames.length <= 3) {
      return memberNames.join(', ');
    }
    
    return `${memberNames.slice(0, 3).join(', ')} +${memberNames.length - 3} more`;
  };

  const formatSubgroupsList = (subgroups) => {
    if (!subgroups || subgroups.length === 0) {
      return 'No subgroups';
    }
    
    // Extract names from DNs before joining
    const subgroupNames = subgroups.map(subgroup => {
      return subgroup.includes('cn=groups') ? subgroup.split(',')[0].replace('cn=', '') :
             subgroup;
    });

    if (subgroupNames.length <= 2) {
      return subgroupNames.join(', ');
    }
    
    return `${subgroupNames.slice(0, 2).join(', ')} +${subgroupNames.length - 2} more`;
  };

  if (!groups || groups.length === 0) {
    return (
      <div className="no-groups">
        <div className="no-groups-content">
          <div className="no-groups-icon">👥</div>
          <h3>No Groups Found</h3>
          <p>There are no groups to display. Create your first group to get started.</p>
          <button className="btn btn-primary" onClick={onRefresh}>
            Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group-table-container">
      <div className="table-header">
        <span className="group-count">{groups.length} group(s)</span>
        <button className="btn btn-outline refresh-btn" onClick={onRefresh}>
          ↻ Refresh
        </button>
      </div>

      <div className="group-table-wrapper">
        <table className="group-table">
          <thead>
            <tr>
              <th></th>
              <th>Group Name</th>
              <th>Description</th>
              <th>Members</th>
              <th>Subgroups</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <React.Fragment key={group.name}>
                <tr className="group-row">
                  <td className="expand-cell">
                    <button
                      className={`expand-btn ${expandedGroups.has(group.name) ? 'expanded' : ''}`}
                      onClick={() => toggleGroupExpansion(group.name)}
                      title={expandedGroups.has(group.name) ? 'Collapse' : 'Expand'}
                    >
                      {expandedGroups.has(group.name) ? '▼' : '▶'}
                    </button>
                  </td>
                  <td className="group-name">
                    <div className="group-name-content">
                      <span className="name">{group.name}</span>
                      {group.gidNumber && (
                        <span className="gid">GID: {group.gidNumber}</span>
                      )}
                    </div>
                  </td>
                  <td className="group-description">
                    {group.description || 'No description'}
                  </td>
                  <td className="group-members">
                    <span className="members-summary">
                      {/* Filter for users only in members summary */}
                      {formatMembersList(group.members?.filter(member => member.includes('cn=users')) || [])}
                    </span>
                    {group.members && group.members.filter(member => member.includes('cn=users')).length > 0 && (
                      <span className="member-count">
                        ({group.members.filter(member => member.includes('cn=users')).length})
                      </span>
                    )}
                  </td>
                  <td className="group-subgroups">
                    <span className="subgroups-summary">
                      {/* Filter for subgroups only in subgroups summary */}
                      {formatSubgroupsList(group.members?.filter(member => member.includes('cn=groups')) || [])}
                    </span>
                     {group.members && group.members.filter(member => member.includes('cn=groups')).length > 0 && (
                      <span className="subgroup-count">
                        ({group.members.filter(member => member.includes('cn=groups')).length})
                      </span>
                    )}
                  </td>
                  <td className="group-actions">
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => onAddSubgroup(group)}
                        title="Add subgroup"
                      >
                        + Sub
                      </button>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => onRemoveUser(group)}
                        title="Remove user"
                        disabled={!group.members || group.members.length === 0}
                      >
                        - User
                      </button>
                    </div>
                  </td>
                </tr>
                
                {expandedGroups.has(group.name) && (
                  <tr className="group-details-row">
                    <td colSpan="6">
                      <div className="group-details">
                        <div className="details-grid">
                          <div className="detail-section">
                            {/* Filter for users only in expanded members list */}
                            <h4>Members ({group.members?.filter(member => member.includes('cn=users')).length || 0})</h4>
                            <div className="detail-content">
                              {group.members?.filter(member => member.includes('cn=users')).length > 0 ? (
                                <ul className="member-list">
                                  {group.members.filter(member => member.includes('cn=users')).map((member, index) => {
                                    const memberName = member.split(',')[0].replace('uid=', '');
                                    return (
                                      <li key={index} className="member-item">
                                        {`User: ${memberName}`}
                                      </li>
                                    );
                                  })}
                                </ul>
                              ) : (
                                <p className="no-items">No members in this group</p>
                              )}
                            </div>
                          </div>
                          
                          <div className="detail-section">
                            {/* Filter for subgroups only in expanded subgroups list */}
                            <h4>Subgroups ({group.members?.filter(member => member.includes('cn=groups')).length || 0})</h4>
                            <div className="detail-content">
                              {group.members?.filter(member => member.includes('cn=groups')).length > 0 ? (
                                <ul className="subgroup-list">
                                  {group.members.filter(member => member.includes('cn=groups')).map((subgroup, index) => {
                                    const subgroupName = subgroup.split(',')[0].replace('cn=', '');
                                    return (
                                      <li key={index} className="subgroup-item">
                                        {`Group: ${subgroupName}`}
                                      </li>
                                    );
                                  })}
                                </ul>
                              ) : (
                                <p className="no-items">No subgroups</p>
                              )}
                            </div>
                          </div>
                          
                          {group.objectClass && (
                            <div className="detail-section">
                              <h4>Object Classes</h4>
                              <div className="detail-content">
                                <div className="object-classes">
                                  {group.objectClass.map((cls, index) => (
                                    <span key={index} className="object-class-tag">
                                      {cls}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GroupTable;
